// src/components/charts/MetricsChart.tsx
import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  ChartBarIcon,
  MusicalNoteIcon,
  UserIcon,
  HeartIcon,
  QueueListIcon
} from '@heroicons/react/24/outline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Define all possible metric types
export type MetricType = 'score' | 'streams' | 'listeners' | 'saves' | 'playlists' | 'saveRate';

// Interface for processed metric data
export interface ProcessedMetric {
  month: string;  // Format: "January 2024" or similar
  score: number;
  streams: number;
  listeners: number;
  saves: number;
  playlists: number;
  saveRate?: number;
  // Change tracking fields (optional)
  streamsChange?: number | null;
  listenersChange?: number | null;
  savesChange?: number | null;
  playlistsChange?: number | null;
  scoreChange?: number | null;
}

interface MetricsChartProps {
  metrics: ProcessedMetric[];
  initialMetricType?: MetricType;
  height?: string;
  showControls?: boolean;
}

// Typen für die Metrik-Konfiguration
interface MetricConfigItem {
  label: string;
  color: string;
  icon: React.ForwardRefExoticComponent<any>;
  scalingFactor: number;
  displayFormat: (value: number) => string;
  maxValue?: number; // Optional für alle Konfigurationen
}

const MetricsChart: React.FC<MetricsChartProps> = ({ 
  metrics, 
  initialMetricType = 'score',
  height = "100%",
  showControls = true
}) => {
  // State for selected metric type
  const [metricType, setMetricType] = useState<MetricType>(initialMetricType);

  // Configuration for different metric types
  const metricConfig: Record<MetricType, MetricConfigItem> = {
    score: {
      label: 'Score',
      color: 'rgb(22, 163, 74)', // green-600
      icon: ChartBarIcon,
      maxValue: 100,   // Score ist auf 100 begrenzt
      scalingFactor: 1.3, // Feiner abgestimmter Skalierungsfaktor
      displayFormat: (value: number) => value.toFixed(2)
    },
    streams: {
      label: 'Streams',
      color: 'rgb(124, 58, 237)', // purple-600
      icon: MusicalNoteIcon,
      scalingFactor: 1.3, // Geringerer Skalierungsfaktor
      displayFormat: (value: number) => value.toLocaleString()
    },
    listeners: {
      label: 'Listeners',
      color: 'rgb(37, 99, 235)', // blue-600
      icon: UserIcon,
      scalingFactor: 1.3, // Geringerer Skalierungsfaktor
      displayFormat: (value: number) => value.toLocaleString()
    },
    saves: {
      label: 'Saves',
      color: 'rgb(219, 39, 119)', // pink-600
      icon: HeartIcon,
      scalingFactor: 1.3, // Geringerer Skalierungsfaktor
      displayFormat: (value: number) => value.toLocaleString()
    },
    playlists: {
      label: 'Playlist Adds',
      color: 'rgb(234, 88, 12)', // orange-600
      icon: QueueListIcon,
      scalingFactor: 1.3, // Geringerer Skalierungsfaktor
      displayFormat: (value: number) => value.toLocaleString()
    },
    saveRate: {
      label: 'Save Rate',
      color: 'rgb(14, 165, 233)', // sky-500
      icon: HeartIcon,
      maxValue: 10,   // Normalerweise ist Save Rate zwischen 0-10%
      scalingFactor: 1.5, // Zusätzlicher Platz für Save Rate
      displayFormat: (value: number) => value.toFixed(2) + '%'
    }
  };

  // Format data for chart display
  const formatChartData = () => {
    if (!metrics || metrics.length === 0) return { labels: [], datasets: [] };

    // Extract month abbreviations for x-axis
    const labels = metrics.map(item => {
      // Convert "January 2024" to "Jan" or use the first 3 chars if already short format
      const monthPart = item.month.split(' ')[0];
      return monthPart.length > 3 ? monthPart.substring(0, 3) : monthPart;
    });

    const data = metrics.map(item => Number(item[metricType] ?? 0));
    const metricInfo = metricConfig[metricType];

    return {
      labels,
      datasets: [
        {
          label: metricInfo.label,
          data,
          borderColor: metricInfo.color,
          backgroundColor: `${metricInfo.color.replace(')', ', 0.1)')}`,
          tension: 0.4,
          fill: true,
          pointBackgroundColor: metricInfo.color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  // Check if data exists for the selected metric
  const hasData = metrics && metrics.some(item => {
    const value = item[metricType];
    return value !== undefined && value !== null && value !== 0;
  });

  // Chart options
  const getChartOptions = (): ChartOptions<'line'> => {
    // Determine the min & max values for the selected metric
    const metricValues = metrics.map(item => Number(item[metricType] ?? 0)); 
    const minValue = Math.min(...metricValues);
    const maxValue = Math.max(...metricValues);
    
    // Konfiguration für den aktuellen Metriktyp
    const config = metricConfig[metricType];
    
    // Calculate dynamic max value for the y-axis scale with better scaling
    let dynamicMax;

    // Optimierter Algorithmus für Y-Achsen-Skalierung
    if (config.maxValue !== undefined && maxValue <= config.maxValue * 0.8) {
      // Wenn der Maximalwert deutlich unter dem konfigurierten Maximum liegt,
      // verwenden wir einen angemesseneren Wert anstelle des vollen Maximums
      dynamicMax = Math.max(maxValue * config.scalingFactor, 10); 
      // Aber nicht mehr als das konfigurierte Maximum
      dynamicMax = Math.min(dynamicMax, config.maxValue);
    } else if (config.maxValue !== undefined) {
      // Bei Werten nahe am Maximum das konfigurierte Maximum verwenden
      dynamicMax = config.maxValue;
    } else {
      // Dynamische Skalierung für andere Metriken
      // Kleinster sinnvoller Wert ist etwa das 1.3-fache des Maximums
      const scalingFactor = Math.max(1.3, Math.min(config.scalingFactor, 1.5));
      
      // Wenn alle Werte sehr klein oder gleich sind, setze ein vernünftiges Minimum
      if (maxValue < 1 || (maxValue === minValue && maxValue === 0)) {
        dynamicMax = 10; // Minimaler sinnvoller Y-Achsenwert
      } else if (maxValue === minValue) {
        // Bei konstanten Werten > 0: etwas Platz hinzufügen
        dynamicMax = maxValue * 1.5;
      } else {
        // Normaler Fall: Skalieren mit Faktor
        dynamicMax = maxValue * scalingFactor;
      }
      
      // Runde dynamicMax auf eine "schöne" Zahl
      const magnitude = Math.pow(10, Math.floor(Math.log10(dynamicMax)));
      dynamicMax = Math.ceil(dynamicMax / magnitude) * magnitude;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 13,
          },
          callbacks: {
            label: function (context: any) {
              const value = context.parsed.y;
              return `${metricConfig[metricType].label}: ${metricConfig[metricType].displayFormat(value)}`;
            }
          }
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: dynamicMax,
            ticks: {
              callback: function(value) {
                return metricConfig[metricType].displayFormat(Number(value));
              }
            },
          grid: {
            drawBorder: false,
          } as any,
        },
        x: {
          grid: {
            display: false,
            drawBorder: false,
          } as any,
        },
      },
    };
  };

  // Function to render metric selector buttons
  const renderMetricButtons = () => {
    return (
      <div className="flex flex-wrap gap-2">
        {Object.entries(metricConfig).map(([key, config]) => {
          const isActive = metricType === key as MetricType;
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setMetricType(key as MetricType)}
              className={`flex items-center px-3 py-1 text-sm font-medium rounded-md ${
                isActive 
                  ? 'bg-green-100 text-green-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className={`h-4 w-4 mr-1 ${isActive ? 'text-green-600' : 'text-gray-500'}`} />
              {config.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-700">Metrics Development</h2>
        {showControls && renderMetricButtons()}
      </div>

      {/* Chart area */}
      <div className="flex-grow" style={{ height: height }}>
        {hasData ? (
          <Line 
            data={formatChartData()} 
            options={getChartOptions()} 
          />
        ) : (
          <div className="flex justify-center items-center h-full">
            <p className="text-gray-500">No {metricConfig[metricType].label.toLowerCase()} history available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsChart;